import { useAtomValue, useSetAtom } from 'jotai';
import uniqueId from 'lodash/uniqueId';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { getNetSuiteContext } from 'pages/requestForm/selectors/netsuite/netSuiteSelectors';
import { useCallback } from 'react';
import { useLazyGetAttachment } from 'shared/data/webApp/v1';
import { netSuiteExpenseLinesState } from 'shared/states';

import { NetSuiteExpenseLineExtendedExpenseReport } from '../../../data/types';
import { reorderLines, setCurrencyPrecision } from '../../../helpers';
import { useChangeNetSuiteRequest, useGetAndIncreaseItemLineNumber, useUpdateLineCustomField } from '../../../hooks';
import { companyIdState, netSuiteItemLineNumberState } from '../../../resources/states';
import { FieldUpdateHandler, MultiLineFieldUpdateHandler } from '../../components';
import {
    checkExpenseLineIsEmpty,
    createEmptyExpenseLineExtended,
    extendExpenseLine,
    getLineCurrencyPrecision,
} from '../../helpers';
import { useExpenseLinesUploadAttachments } from '../useExpenseLinesUploadAttachments/useExpenseLinesUploadAttachments';
import { getAttachmentNameWithPostfix, getMaxRefLineNumber } from './useExpenseReportExpenseLines.helpers';

export const useExpenseReportExpenseLines = () => {
    const [getAttachment] = useLazyGetAttachment();

    const { onUploadAttachment } = useExpenseLinesUploadAttachments();

    const setExpenseLines = useSetAtom(netSuiteExpenseLinesState);
    const companyId = useAtomValue(companyIdState);

    const { currencies } = useSelector(getNetSuiteContext);

    const getAndIncreaseItemLineNumber = useGetAndIncreaseItemLineNumber();
    const setNextLineNumber = useSetAtom(netSuiteItemLineNumberState);

    const changeNetSuiteRequest = useChangeNetSuiteRequest();

    const setManyExpenseLines = useCallback(
        (lines: domain.NetSuiteExpenseLineExpenseReport[], currencyPrecision: number) => {
            if (lines.length === 0) {
                const lineNumber = getAndIncreaseItemLineNumber();

                setExpenseLines([createEmptyExpenseLineExtended(lineNumber)]);
            } else {
                let maxLineNumber = 0;

                setExpenseLines(
                    lines.map((line) => {
                        const extendedLine = extendExpenseLine(line);

                        const lineCurrencyPrecision = getLineCurrencyPrecision(extendedLine, currencies);

                        if (extendedLine.lineNumber > maxLineNumber) {
                            maxLineNumber = extendedLine.lineNumber;
                        }

                        return {
                            ...extendedLine,
                            amount: setCurrencyPrecision(extendedLine.amount, currencyPrecision),
                            taxAmount: setCurrencyPrecision(extendedLine.taxAmount, currencyPrecision),
                            grossAmount: setCurrencyPrecision(extendedLine.grossAmount, currencyPrecision),
                            foreignAmount: setCurrencyPrecision(extendedLine.foreignAmount, lineCurrencyPrecision),
                        };
                    })
                );

                setNextLineNumber(maxLineNumber + 1);
            }
        },
        [currencies, getAndIncreaseItemLineNumber, setExpenseLines, setNextLineNumber]
    );

    const onFieldUpdate = useCallback<FieldUpdateHandler>(
        (id, key, value) => {
            setExpenseLines((current) =>
                current.map((item) => {
                    if (item.id === id) {
                        const newItem = { ...item, [key]: value };

                        if (key === 'customer') {
                            newItem.isBillable = value !== null ? item.isBillable : false;
                        }

                        newItem.isEmpty = checkExpenseLineIsEmpty(newItem);

                        return newItem;
                    }

                    return item;
                })
            );

            changeNetSuiteRequest();
        },
        [setExpenseLines, changeNetSuiteRequest]
    );

    const onMultiLineFieldUpdate = useCallback<MultiLineFieldUpdateHandler>(
        (key, value) => {
            setExpenseLines((current) =>
                current.map((item) => {
                    const newItem = { ...item, [key]: value };

                    if (key === 'customer') {
                        newItem.isBillable = value !== null ? item.isBillable : false;
                    }

                    newItem.isEmpty = checkExpenseLineIsEmpty(newItem);

                    return newItem;
                })
            );

            changeNetSuiteRequest();
        },
        [setExpenseLines, changeNetSuiteRequest]
    );

    const setOneExpenseLine = useCallback(
        (lineId: string, data: Partial<NetSuiteExpenseLineExtendedExpenseReport>) => {
            setExpenseLines((linesPrev) =>
                linesPrev.map((line) => {
                    if (line.id === lineId) {
                        const updatedItem = { ...line, ...data };

                        updatedItem.isEmpty = checkExpenseLineIsEmpty(updatedItem);

                        return updatedItem;
                    }

                    return line;
                })
            );

            changeNetSuiteRequest();
        },
        [changeNetSuiteRequest, setExpenseLines]
    );

    const setAllExpenseLines = useCallback(
        (data: Partial<NetSuiteExpenseLineExtendedExpenseReport>) => {
            setExpenseLines((linesPrev) =>
                linesPrev.map((line) => {
                    const updatedItem = { ...line, ...data };

                    updatedItem.isEmpty = checkExpenseLineIsEmpty(updatedItem);

                    return updatedItem;

                    return line;
                })
            );

            changeNetSuiteRequest();
        },
        [changeNetSuiteRequest, setExpenseLines]
    );

    const updateCustomField = useUpdateLineCustomField(setExpenseLines);

    const onAddLine = useCallback(() => {
        setExpenseLines((lines) => {
            const lineNumber = getAndIncreaseItemLineNumber();
            const refLineNumber = getMaxRefLineNumber(lines) + 1;

            return [...lines, createEmptyExpenseLineExtended(lineNumber, refLineNumber)];
        });

        changeNetSuiteRequest();
    }, [changeNetSuiteRequest, getAndIncreaseItemLineNumber, setExpenseLines]);

    const onRemoveLine = useCallback(
        (id: string) => {
            setExpenseLines((current) => {
                const expenseItems = current.filter((item) => item.id !== id);

                if (expenseItems.length === 0) {
                    const lineNumber = getAndIncreaseItemLineNumber();

                    return [createEmptyExpenseLineExtended(lineNumber)];
                }

                return expenseItems;
            });

            changeNetSuiteRequest();
        },
        [changeNetSuiteRequest, getAndIncreaseItemLineNumber, setExpenseLines]
    );

    const reuploadAttachment = useCallback(
        async (lineId: string, attachment: domain.RequestAttachment) => {
            const attachedFile = await getAttachment({
                params: { query: { companyId: companyId || '', attachmentId: attachment.id } },
            });

            const filesWithRequestUuid = [new File([attachedFile], attachment.name)];

            const reuploadedAttachment = await onUploadAttachment(lineId, filesWithRequestUuid);

            setOneExpenseLine(lineId, { attachment: reuploadedAttachment });
        },
        [companyId, getAttachment, onUploadAttachment, setOneExpenseLine]
    );

    const onCloneLine = useCallback(
        (id: string) => {
            setExpenseLines((lines) => {
                const targetItem = lines.find((item) => item.id === id);
                const lineId = uniqueId('netSuiteExpenseLine-');

                if (targetItem) {
                    const lineNumber = getAndIncreaseItemLineNumber();
                    const refLineNumber = getMaxRefLineNumber(lines) + 1;
                    const attachment = targetItem.attachment && {
                        ...targetItem.attachment,
                        name: getAttachmentNameWithPostfix(targetItem.attachment.name, refLineNumber),
                        createdDate: Date.now().toString(),
                    };

                    if (attachment) {
                        void reuploadAttachment(lineId, attachment);
                    }

                    return [
                        ...lines,
                        {
                            ...targetItem,
                            id: lineId,
                            refLineNumber,
                            lineNumber,
                            attachment,
                        },
                    ];
                }

                return lines;
            });

            changeNetSuiteRequest();
        },
        [changeNetSuiteRequest, getAndIncreaseItemLineNumber, reuploadAttachment, setExpenseLines]
    );

    const onReorder = useCallback(
        (oldIndex: number, newIndex: number) => {
            setExpenseLines((lines) => reorderLines(lines, oldIndex, newIndex));

            changeNetSuiteRequest();
        },
        [changeNetSuiteRequest, setExpenseLines]
    );

    const handleReorderRows = useCallback(
        <Rows extends Record<string, any>[]>(_reorderedRows: Rows, oldIndex: number, newIndex: number) => {
            onReorder(oldIndex, newIndex);
        },
        [onReorder]
    );

    return {
        setManyExpenseLines,
        setOneExpenseLine,
        setAllExpenseLines,
        setExpenseLines,
        updateCustomField,
        onFieldUpdate,
        onMultiLineFieldUpdate,
        onAddLine,
        onRemoveLine,
        onCloneLine,
        onReorder,
        handleReorderRows,
    };
};
